import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, OnInit } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { translate, TranslocoService } from '@jsverse/transloco';
import moment from 'moment';
import { CalendarData, Calendar_onValueChangeEvent, DropdownObject } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import { RelativeDateConstants } from 'app/constants/constants';
import {
  DATE_TIME_WITHOUT_TIMEZONE_FORMAT,
  FilterService,
} from 'app/modules/collection/services/filter.service';
import { SemantriaUsersService } from 'app/providers/semantria-api/semantria-users.service';
import {
  AuditEventType,
  AuditService,
  REPORTABLE_AUDIT_EVENTS,
} from 'app/providers/storage-api/audit.service';
import { AlertService } from 'app/services/alert.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { TimezoneService } from 'app/services/timezone.service';

interface ReportTableEntry {
  event: AuditEventType;
  userId: string;
  timestamp: Date;
  properties?: { [key: string]: string };
}

@EnableClassTelemetry()
@Component({
  selector: 'app-reports',
  imports: [AppSharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],
})
export class ReportsComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  eventTypes: DropdownObject[] = [];
  selectedEventType: string;

  calendarData: CalendarData;
  relativeRangeId = RelativeDateConstants.LAST_7_DAY;
  startDate: string;
  endDate: string;

  displayedColumns = ['event', 'user', 'timestamp', 'properties'];
  dataSource: MatTableDataSource<ReportTableEntry>;
  expandedEvent: ReportTableEntry;
  usersById = new Map<string, string>();
  paging: {
    count: number;
    currentPage: number;
    pageSize: number;
  } = {
    count: 0,
    currentPage: 0,
    pageSize: 10,
  };

  isLoading: boolean;

  private filterService = inject(FilterService);
  private timezoneService = inject(TimezoneService);
  private auditService = inject(AuditService);
  private userService = inject(SemantriaUsersService);
  private alertService = inject(AlertService);
  private translocoService = inject(TranslocoService);

  constructor() {
    this.calendarData = {
      preselectedCalendarListOptions: this.filterService.generateRelativeRangeOptions(),
    };
    [this.startDate, this.endDate] = this.filterService.computeFixedValueForDateFilter(
      this.relativeRangeId
    );
  }

  ngOnInit(): void {
    this.translocoService
      .selectTranslateObject('reports.audit-events')
      .subscribe((translations) => {
        this.eventTypes = Array.from(new Set(REPORTABLE_AUDIT_EVENTS.map((e) => e.split('-')[0])))
          .concat(REPORTABLE_AUDIT_EVENTS)
          .sort()
          .map((e) => ({
            id: e,
            title: translations[e],
          }));
        this.eventTypes.unshift({
          id: '',
          title: translations['all-events'],
        });
      });
  }

  onCalendarValueChange(eventDetail: Calendar_onValueChangeEvent): void {
    this.startDate = moment(eventDetail.dates.first)
      .tz(this.timezoneService.timezone, true)
      .format(DATE_TIME_WITHOUT_TIMEZONE_FORMAT);
    this.endDate = moment(eventDetail.dates.last)
      .tz(this.timezoneService.timezone, true)
      .format(DATE_TIME_WITHOUT_TIMEZONE_FORMAT);
  }

  previewReport() {
    this.paging = {
      count: 0,
      currentPage: 0,
      pageSize: this.paging.pageSize,
    };

    this.loadAuditEvents();
  }

  onPageChanged($event: PageEvent) {
    this.paging.currentPage = $event.pageIndex;
    this.paging.pageSize = $event.pageSize;

    this.loadAuditEvents();
  }

  private loadAuditEvents() {
    this.isLoading = true;
    const from = moment(this.startDate).tz(this.timezoneService.timezone, true).format();
    const to = moment(this.endDate).tz(this.timezoneService.timezone, true).format();

    this.auditService
      .listAuditEvents(
        this.selectedEventType,
        from,
        to,
        this.paging.currentPage * this.paging.pageSize,
        this.paging.pageSize
      )
      .then((res) => {
        this.dataSource = new MatTableDataSource(
          res.items.map((e) => ({
            event: e.name,
            userId: e.properties?.userId,
            timestamp: new Date(e.timestamp),
            properties: e.properties,
          }))
        );
        this.paging.count = res.count;

        return [...new Set(this.dataSource.data.filter((d) => !!d.userId).map((d) => d.userId))];
      })
      .then((users) => {
        for (const user of users) {
          if (!this.usersById.has(user)) {
            this.userService
              .getDisplayNameForUser(user)
              .then((res) => {
                this.usersById.set(user, res);
              })
              .catch((err) => {
                console.warn('Failed to get display name for user', user, err);

                this.usersById.set(user, null);
              });
          }
        }
      })
      .finally(() => (this.isLoading = false));
  }

  generateReport() {
    this.isLoading = true;

    this.auditService
      .createAuditReportTask(
        this.selectedEventType,
        this.startDate,
        this.endDate,
        this.timezoneService.timezone
      )
      .then((_result) => {
        this.alertService.showToast(
          translate('reports.report-being-generated'),
          undefined,
          undefined,
          undefined,
          'open-download-center-pointer',
          'open-download-center'
        );
      })
      .catch((error) => {
        this.alertService.showErrorToast(translate('reports.report-generate-failed'), error);
      })
      .finally(() => (this.isLoading = false));
  }

  get canDoReport() {
    return this.selectedEventType !== undefined && !this.isLoading;
  }
}
